import classNames from 'classnames';
import ouibounce from 'ouibounce';
import React, {useContext, useEffect, useRef} from 'react';

import {InDemandCareer, InDemandCareerWrapper} from '@udemy/career-academies';
import {AboveTheFoldProvider} from '@udemy/design-system-utils';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {TestimonialsCMSWrapper} from '@udemy/react-cms-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {
    CourseGuidanceEntryPointWithPreselection,
    EnhancedCategoriesComponent,
    EntryPointPopUp,
    TopCategoriesComponent,
} from '@udemy/react-globalization-components';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
    OnePaneLayoutFullWidthSectionContent,
} from '@udemy/react-layout-components';
import {
    CMSConnectedPartnersBanner,
    DynamicWebContentPageType,
} from '@udemy/shared-one-udemy-components';
import {useUDData} from '@udemy/ud-data';

import {AIEraCareerBanner} from 'src/components/ai-career-banner/ai-career-banner.react-component';
import {DiscoveryUnits} from 'src/components/discovery-units/discovery-units.react-component';
import {EnrichedSkillsContainer} from 'src/components/enriched-skills/enriched-skills-container.react-component';
import {MarketingHeroBanner} from 'src/components/hero-banner/index';
import {PlanComparison} from 'src/components/plan-comparison/plan-comparison.react-component';
import {
    ProfessionalSkillsUnit,
    PSUSkeleton,
} from 'src/components/professional-skills-showcase/professional-skills-showcase.react-component';
import {TrendingSkills} from 'src/components/trending-skills/trending-skills.react-component';
import {UBCaseStudiesCMSWrapper} from 'src/components/ub-case-studies/ub-case-studies-cms-wrapper.react-component';
import {ValuePropModule} from 'src/components/value-prop-module/value-prop-module.react-component';
import {
    CAREER_ACADEMIES,
    COURSE_GUIDANCE_FEATURE,
    LEARNERS_ARE_VIEWING_ON_LOHP,
    LOHP_VERTICAL_MOBILE_RECO_UNITS,
    POPULAR_CATEGORIES_MODULE,
    POPULAR_CATEGORIES_MODULE_FOR_LOHP,
    SHOW_TRENDING_SKILLS_ABOVE_PARTNERS_BANNER,
    REIMAGINE_LOHP,
} from 'src/lib/experimentation-helper';

import {CertPrepModule} from '../../components/cert-prep-module/cert-prep-module.react-component';
import {UBReportsBannerCMSWrapper} from '../../components/ub-reports-banner/ub-reports-banner-cms-wrapper.react-component';
import {ValuePropsAndFeaturesCMSWrapper} from '../../components/value-props-and-features/value-props-and-features-cms-wrapper.react-component';
import {ProfessionalSkillsShowcaseDataContext} from './contexts/professional-skills-showcase-data-context';
import {NOTICE_TYPES, useFetchHeroBanners} from './hooks/useFetchHeroBanners';
import {useVerifyIsUsMarketplace} from './hooks/useVerifyIsUsMarketplace';
import styles from './logged-out-homepage-body.module.less';

export interface LoggedOutHomePageBodyProps {
    academies: InDemandCareer[];
}

export const LoggedOutHomePageBody = ({academies}: LoggedOutHomePageBodyProps) => {
    const isMobileMax = useMatchMedia('mobile-max');
    const {heroBanners, isHeroBannerLoading, noticeType} = useFetchHeroBanners(
        NOTICE_TYPES.BANNER_CAROUSEL_SLIDE,
    );
    const {locale, gettext} = useI18n();
    const isLanguageLocaleJapanese = locale === 'ja_JP';
    const {isUSMarketplace} = useVerifyIsUsMarketplace(locale);
    const psuData = useContext(ProfessionalSkillsShowcaseDataContext);
    const {me, Config: udConfig} = useUDData();
    const hasTrackedExitIntent = useRef(false);
    const isPriceCountryJapan = udConfig.price_country?.id === 'JP';
    // PSU re-ordering Experiment - https://udemyjira.atlassian.net/browse/TB-10066
    const {isLoading, featureVariantAssignments: features} = useFeatures([
        CAREER_ACADEMIES,
        COURSE_GUIDANCE_FEATURE,
        LEARNERS_ARE_VIEWING_ON_LOHP,
        POPULAR_CATEGORIES_MODULE,
        POPULAR_CATEGORIES_MODULE_FOR_LOHP,
        SHOW_TRENDING_SKILLS_ABOVE_PARTNERS_BANNER,
        LOHP_VERTICAL_MOBILE_RECO_UNITS,
        REIMAGINE_LOHP,
    ]);
    const showCourseGuidanceFeature = features.get(COURSE_GUIDANCE_FEATURE)?.configuration ?? {};
    const showPopularCategoriesModule =
        features.get(POPULAR_CATEGORIES_MODULE)?.configuration ?? {};
    const showTrendingSkillsAbovePartnersBanner =
        features.get(SHOW_TRENDING_SKILLS_ABOVE_PARTNERS_BANNER)?.configuration ?? {};
    const showReimagineLohp =
        features.get(REIMAGINE_LOHP)?.configuration?.showUpdatedHomepage ?? false;

    // Learners Also Viewing | PSU Features - https://udemyjira.atlassian.net/browse/CVR-523
    const learnersViewingFeatConfigVal = features.get(LEARNERS_ARE_VIEWING_ON_LOHP);

    // Retrieve feature configuration values from the ExperimentationContext features object
    const learnersViewingUnitPlacement = learnersViewingFeatConfigVal?.configuration?.placement;

    // Hide Learners Also Viewing Section on certain locales with low course supply
    const shouldHidePSUOnLowCourseSupplyLocale = ['nl_NL', 'ro_RO', 'th_TH', 'vi_VN'].includes(
        locale,
    );

    const isCareerAcademiesEnabled = features.get(CAREER_ACADEMIES)?.configuration?.feature;
    const displayedAcademies = academies.length > 3 ? academies.slice(0, 3) : academies;

    const isVerticalRecoUnitsFeatureEnabled = features.get(LOHP_VERTICAL_MOBILE_RECO_UNITS)
        ?.configuration?.useVerticalLayout;

    const showAddToCartButtonsInVerticalLayout = features.get(LOHP_VERTICAL_MOBILE_RECO_UNITS)
        ?.configuration?.showAddToCartButtons;

    useEffect(() => {
        const dummyModal = document.createElement('div');

        ouibounce(dummyModal, {
            aggressive: true,
            timer: 0,
            callback: () => {
                if (me.is_authenticated || hasTrackedExitIntent.current) return;
                window.appboy?.logCustomEvent('exit_intent', {page: 'LoggedOutHomePage'});
                hasTrackedExitIntent.current = true;
            },
        });
    }, [me.is_authenticated]);

    return (
        <div data-testid="lohp-body">
            <OnePaneLayoutContainer>
                <AboveTheFoldProvider>
                    <section>
                        <MarketingHeroBanner
                            heroBanners={heroBanners}
                            noticeType={noticeType}
                            isLoading={isHeroBannerLoading}
                            showBannerSearchBar={true}
                        />
                    </section>

                    {showReimagineLohp && <EnrichedSkillsContainer />}
                    {isLanguageLocaleJapanese && <ValuePropModule />}
                    {showReimagineLohp && <AIEraCareerBanner />}

                    {!shouldHidePSUOnLowCourseSupplyLocale && showReimagineLohp && (
                        <section className={styles['professionals-skills-module']}>
                            <ProfessionalSkillsUnit
                                pageType="logged_out_homepage"
                                // Needed to pass this for preventing gray background when padding is added
                                contentPanelClassName={styles['psu-subcontent-panel']}
                                unitData={psuData}
                                loadingSkeleton={isLoading ? <PSUSkeleton /> : null}
                                isVerticalRecoUnitsFeatureEnabled={
                                    isVerticalRecoUnitsFeatureEnabled
                                }
                                showAddToCartButtonsInVerticalLayout={
                                    showAddToCartButtonsInVerticalLayout
                                }
                                showReimagineLohp={showReimagineLohp}
                            />
                        </section>
                    )}

                    {showReimagineLohp && (
                        <section>
                            <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                                <div className={styles['partners-banner-wrapper']}>
                                    <CMSConnectedPartnersBanner
                                        pageType={DynamicWebContentPageType.LoggedOutHomePage}
                                    />
                                </div>
                            </OnePaneLayoutFullWidthSection>
                        </section>
                    )}

                    {showReimagineLohp && (
                        <section>
                            <OnePaneLayoutFullWidthSection>
                                <div className={styles['ud-container-with-common-padding']}>
                                    <TestimonialsCMSWrapper
                                        {...(showReimagineLohp
                                            ? {
                                                  showCta: !isMobileMax,
                                                  ctaHref: 'https://about.udemy.com/stories/',
                                                  ctaLabel: gettext('View all stories'),
                                                  titleClassName: 'ud-heading-xl',
                                              }
                                            : {})}
                                    />
                                </div>
                            </OnePaneLayoutFullWidthSection>
                        </section>
                    )}

                    {showReimagineLohp && <CertPrepModule />}

                    {isCareerAcademiesEnabled && !!displayedAcademies?.length && (
                        <section className={styles['academies-list']}>
                            <h2
                                className={classNames(
                                    showReimagineLohp ? 'ud-heading-xxl' : 'ud-heading-serif-xxl',
                                    styles['academies-list-heading'],
                                )}
                            >
                                {gettext('Ready to reimagine your career?')}
                            </h2>
                            <div className={styles['academies-list-subtitle']}>
                                {gettext(
                                    'Get the skills and real-world experience employers want with Career Accelerators.',
                                )}
                            </div>
                            <InDemandCareerWrapper
                                academies={displayedAcademies}
                                className={styles['academies-list-wrapper']}
                                headingProps={{className: styles['hide']}}
                                isUSMarketplace={isUSMarketplace}
                                {...(showReimagineLohp
                                    ? {
                                          buttonProps: {udStyle: 'ghost'},
                                      }
                                    : {})}
                            />
                        </section>
                    )}
                </AboveTheFoldProvider>

                {showCourseGuidanceFeature.show_course_guidance_feature && !showReimagineLohp && (
                    <section className={styles['guidance-entry-point']}>
                        <CourseGuidanceEntryPointWithPreselection />
                    </section>
                )}
                <EntryPointPopUp />
                {(showPopularCategoriesModule.show_enhanced_categories_module ||
                    showPopularCategoriesModule.show_enhanced_categories_module_for_mobile) && (
                    <section className={styles['top-categories']}>
                        <EnhancedCategoriesComponent />
                    </section>
                )}
                {isPriceCountryJapan &&
                    !showPopularCategoriesModule.show_enhanced_categories_module &&
                    !showPopularCategoriesModule.show_enhanced_categories_module_for_mobile && (
                        <section className={styles['top-categories']}>
                            <TopCategoriesComponent />
                        </section>
                    )}
                {!shouldHidePSUOnLowCourseSupplyLocale && !showReimagineLohp && (
                    <section className={styles['professionals-skills-module']}>
                        <ProfessionalSkillsUnit
                            pageType="logged_out_homepage"
                            // Needed to pass this for preventing gray background when padding is added
                            contentPanelClassName={styles['psu-subcontent-panel']}
                            unitData={psuData}
                            loadingSkeleton={isLoading ? <PSUSkeleton /> : null}
                            isVerticalRecoUnitsFeatureEnabled={isVerticalRecoUnitsFeatureEnabled}
                            showAddToCartButtonsInVerticalLayout={
                                showAddToCartButtonsInVerticalLayout
                            }
                            showReimagineLohp={showReimagineLohp}
                        />
                    </section>
                )}
                {showTrendingSkillsAbovePartnersBanner.show_trending_skills_above_partners_banner && (
                    <section>
                        <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                            <div className={styles['ud-container-with-common-padding']}>
                                <TrendingSkills showReimagineLohp={showReimagineLohp} />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>
                )}
                {!showReimagineLohp && (
                    <section>
                        <div className={styles['partners-banner-wrapper']}>
                            <CMSConnectedPartnersBanner
                                pageType={DynamicWebContentPageType.LoggedOutHomePage}
                            />
                        </div>
                    </section>
                )}
                {learnersViewingUnitPlacement === 'below_professional_skill_showcase_unit' &&
                    !showReimagineLohp && (
                        <OnePaneLayoutFullWidthSection>
                            <section
                                id="discovery-units-top"
                                className={styles['ud-container-with-common-padding']}
                            >
                                <DiscoveryUnits
                                    isVerticalRecoUnitsFeatureEnabled={
                                        isVerticalRecoUnitsFeatureEnabled
                                    }
                                    showAddToCartButtonsInVerticalLayout={
                                        showAddToCartButtonsInVerticalLayout
                                    }
                                    showReimagineLohp={showReimagineLohp}
                                />
                            </section>
                        </OnePaneLayoutFullWidthSection>
                    )}
                {!showReimagineLohp && (
                    <section>
                        <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                            <div className={styles['ud-container-with-common-padding']}>
                                <ValuePropsAndFeaturesCMSWrapper />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>
                )}
                {showReimagineLohp && (
                    <OnePaneLayoutFullWidthSection>
                        <section
                            id="discovery-units-top"
                            className={styles['ud-container-with-common-padding']}
                        >
                            <DiscoveryUnits
                                isVerticalRecoUnitsFeatureEnabled={
                                    isVerticalRecoUnitsFeatureEnabled
                                }
                                showAddToCartButtonsInVerticalLayout={
                                    showAddToCartButtonsInVerticalLayout
                                }
                                showReimagineLohp={showReimagineLohp}
                            />
                        </section>
                    </OnePaneLayoutFullWidthSection>
                )}
                <OnePaneLayoutFullWidthSection
                    className={showReimagineLohp ? styles['fill-background'] : undefined}
                >
                    <PlanComparison showReimagineLohp={showReimagineLohp} />
                </OnePaneLayoutFullWidthSection>
                {!showReimagineLohp && (
                    <section>
                        <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                            <div className={styles['ud-container-with-common-padding']}>
                                <TestimonialsCMSWrapper
                                    {...(showReimagineLohp
                                        ? {
                                              showCta: !isMobileMax,
                                              ctaHref: 'https://about.udemy.com/stories/',
                                              ctaLabel: gettext('View all stories'),
                                              titleClassName: 'ud-heading-xl',
                                          }
                                        : {})}
                                />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>
                )}
                <section>
                    <UBReportsBannerCMSWrapper
                        {...(showReimagineLohp
                            ? {
                                  headingClassName: 'ud-heading-xxl',
                              }
                            : {})}
                    />
                </section>
                {!showTrendingSkillsAbovePartnersBanner.show_trending_skills_above_partners_banner && (
                    <section>
                        <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                            <div className={styles['ud-container-with-common-padding']}>
                                <TrendingSkills showReimagineLohp={showReimagineLohp} />
                            </div>
                        </OnePaneLayoutFullWidthSection>
                    </section>
                )}
                {learnersViewingUnitPlacement === 'default' && !showReimagineLohp && (
                    <OnePaneLayoutFullWidthSection>
                        <section
                            id="discovery-units-lower"
                            className={styles['ud-container-with-common-padding']}
                        >
                            <DiscoveryUnits
                                isVerticalRecoUnitsFeatureEnabled={
                                    isVerticalRecoUnitsFeatureEnabled
                                }
                                showAddToCartButtonsInVerticalLayout={
                                    showAddToCartButtonsInVerticalLayout
                                }
                                showReimagineLohp={showReimagineLohp}
                            />
                        </section>
                    </OnePaneLayoutFullWidthSection>
                )}
                <section>
                    <OnePaneLayoutFullWidthSection className={styles['fill-background']}>
                        <OnePaneLayoutFullWidthSectionContent className={styles['ub-case-studies']}>
                            <UBCaseStudiesCMSWrapper />
                        </OnePaneLayoutFullWidthSectionContent>
                    </OnePaneLayoutFullWidthSection>
                </section>
            </OnePaneLayoutContainer>
        </div>
    );
};
